import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { Pencil, Trash2 } from 'lucide-react';
import type { ChatSummary } from '@medicail/shared';
import { cn } from '@/lib/cn';
import { ConfirmDialog, IconButton } from '@/components/ui';
import { useDeleteChat, useRenameChat } from '@/features/chat/api';

export function ChatListItem({ chat, active, onNavigate }: { chat: ChatSummary; active: boolean; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const rename = useRenameChat();
  const del = useDeleteChat();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(chat.title);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const settled = useRef(false);

  useEffect(() => {
    if (editing) {
      settled.current = false;
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    if (settled.current) return;
    settled.current = true;
    const title = draft.trim().slice(0, 120);
    setEditing(false);
    if (title && title !== chat.title) rename.mutate({ chatId: chat.chatId, title });
  };

  const cancel = () => {
    settled.current = true;
    setDraft(chat.title);
    setEditing(false);
  };

  return (
    <li className="group relative">
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          maxLength={120}
          aria-label="Chat title"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          className="h-10 w-full rounded-xl border border-royal-300 bg-surface px-3 text-sm text-ink focus:outline-none focus:ring-4 focus:ring-royal-500/20"
        />
      ) : (
        <>
          <NavLink
            to={`/c/${chat.chatId}`}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex h-10 items-center rounded-xl px-3 text-sm transition-colors',
              active ? 'bg-royal-50 font-medium text-royal-700 dark:bg-royal-500/20 dark:text-royal-100' : 'text-ink hover:bg-hover',
            )}
          >
            <span className="min-w-0 flex-1 truncate group-hover:pr-14 group-focus-within:pr-14 [@media(hover:none)]:pr-14">{chat.title || 'New chat'}</span>
          </NavLink>
          <div className="absolute inset-y-0 right-1 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
            <IconButton
              label="Rename chat"
              size="icon-sm"
              tooltip={false}
              onClick={() => {
                setDraft(chat.title);
                setEditing(true);
              }}
              className="text-ink-muted"
            >
              <Pencil className="size-3.5" />
            </IconButton>
            <IconButton label="Delete chat" size="icon-sm" tooltip={false} onClick={() => setConfirming(true)} className="text-ink-muted hover:text-danger-600">
              <Trash2 className="size-3.5" />
            </IconButton>
          </div>
        </>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete this chat?"
        description={
          <>
            <strong className="font-medium text-ink">{chat.title || 'New chat'}</strong> and its attachments will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete chat"
        loading={del.isPending}
        onConfirm={() => {
          del.mutate(chat.chatId);
          setConfirming(false);
          if (active) navigate('/');
        }}
      />
    </li>
  );
}
